import { useEffect, useState } from 'react';
import {
    Box, Typography, TextField, Button, Switch, FormControlLabel, IconButton,
    Paper, Divider, Alert, CircularProgress, Tooltip, Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CampaignIcon from '@mui/icons-material/Campaign';
import {
    getClapsleySettings, saveClapsleySettings,
    sendLeaderboardNow, sendClapsleyTestMessage, sendTelegramTest, sendAnnouncement, sendPricingDigestNow,
    sendClaimEmailNow,
} from '../../api/clapsleyApi';

// Standardized message templates shared by BOTH channels (WhatsApp + Telegram). One template
// per event; the backend renders it once and fans out to whichever channels are enabled for
// that event. (user 2026-07-26)
const DEFAULT_NEW_SALE_TEMPLATE =
`👏 Congrats {agent_name} for new case registration!
📍 {kwp}KWP - {city},{state} - {deposit_date}
Keep it up & let's close more deals!🌊 Towards No.1`;

const DEFAULT_LEADERBOARD_TEMPLATE =
`🏆 *Top 5 Agents — {month}* (month-to-date)

{leaderboard}

Keep pushing! 🌊 Towards No.1`;

const DEFAULT_NOTICE_TEMPLATE =
`📢 *{title}*

{content}

Posted by {author} · {date}
🌊 Towards No.1`;

const DEFAULT_ONBOARDING_TEMPLATE =
`halo Janny, here's our new member on-boarding!
{name} - {phone}
Upline: {upline} - {uplinephone}
Team: {team}

Thankyou o`;

const DEFAULT_WELCOME_MEMBER_TEMPLATE =
`Hi {name},
Welcome to nexwave team, we are heading towards No.1 in solar industry in Malaysia.

Here's your sales platform:
1. www.sales.nexwaveintl.com
2. you may check tutorial here.

Thanks! Happy selling.
Towards No.1`;

const DEFAULT_ANNOUNCEMENT_TEMPLATE =
`📢 *System Update*

{content}

Questions? Reply here.
🌊 Towards No.1`;

const PP = "'Poppins', sans-serif";
const cardSx = { p: 3, mb: 2.5, borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' };

// Recipient row editor — a list of strings, each editable, with add/remove.
function RecipientList({ label, value, onChange, helperText, placeholder, emptyNote }) {
    const items = Array.isArray(value) ? value : [];
    const updateAt = (i, v) => onChange(items.map((x, idx) => idx === i ? v : x));
    const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, '']);
    return (
        <Box sx={{ mt: 1.5 }}>
            {label && <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', fontFamily: PP, mb: 0.3 }}>{label}</Typography>}
            {helperText && <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mb: 0.75 }}>{helperText}</Typography>}
            {items.length === 0 && emptyNote !== null && <Typography sx={{ fontSize: '0.74rem', color: '#94A3B8', fontStyle: 'italic', fontFamily: PP, mb: 0.75 }}>{emptyNote || '(empty — nothing sent on this channel)'}</Typography>}
            {items.map((v, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'center' }}>
                    <TextField size="small" fullWidth value={v} onChange={(e) => updateAt(i, e.target.value)}
                        placeholder={placeholder || '120363…@g.us  ·  60xxxxxxxxx@s.whatsapp.net  ·  +60xxxxxxxxx'}
                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.8rem' } }} />
                    <Tooltip title="Remove"><IconButton size="small" onClick={() => removeAt(i)} sx={{ color: '#94A3B8' }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ fontFamily: PP, textTransform: 'none', color: '#1A8CFF' }}>Add recipient</Button>
        </Box>
    );
}

// A per-channel target panel (WhatsApp or Telegram) inside an event card: header + toggle +
// its own targeting (WhatsApp recipient list / Telegram destination note).
// Shared channel-panel header (circle icon + title + optional toggle) — used by both the
// Channels section and every event card so the WhatsApp/Telegram look is identical.
function ChannelHeader({ color, icon, name, enabled, onToggle, disabled, chip }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.8rem' }}>{icon}</Box>
                <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', fontWeight: 700, color }}>{name}</Typography>
            </Box>
            {onToggle
                ? <Switch size="small" disabled={disabled} checked={!!enabled} onChange={e => onToggle(e.target.checked)} />
                : (chip ? <Chip size="small" label={chip} sx={{ fontFamily: PP, fontSize: '0.6rem', height: 18, bgcolor: '#F1F5F9' }} /> : null)}
        </Box>
    );
}

function ChannelPanel({ color, icon, name, enabled, onToggle, disabled, chip, children }) {
    const dim = onToggle && !enabled;
    return (
        <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.5, borderLeft: `3px solid ${color}`, opacity: dim ? 0.6 : 1, transition: 'opacity .15s' }}>
            <ChannelHeader color={color} icon={icon} name={name} enabled={enabled} onToggle={onToggle} disabled={disabled} chip={chip} />
            {children}
        </Box>
    );
}

// One event card: header + SHARED template (top), then WhatsApp | Telegram targets (two columns).
function EventCard({ icon, title, desc, vars, wa, onWa, tg, onTg, tgDisabled, tgRecipients, onTgRecipients, template, onTemplate, defaultTemplate, minRows = 4, recipients, onRecipients, recipientHelp, waSelfNote }) {
    return (
        <Paper sx={cardSx}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>{icon} {title}</Typography>
            {desc && <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mt: 0.3 }}>{desc}</Typography>}
            {/* Shared template */}
            {vars && <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP, mt: 1.25, mb: 0.5 }}><b>Shared template</b> · Variables: {vars}</Typography>}
            <TextField size="small" fullWidth multiline minRows={minRows} value={template || ''} onChange={e => onTemplate(e.target.value)}
                InputProps={{ sx: { fontFamily: 'Consolas, monospace', fontSize: '0.82rem' } }} />
            <Button size="small" startIcon={<RestartAltIcon />} onClick={() => onTemplate(defaultTemplate)}
                sx={{ mt: 0.5, fontFamily: PP, textTransform: 'none', color: '#64748B' }}>Reset to default</Button>
            {/* Per-channel targets */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
                <ChannelPanel color="#128C7E" icon="💬" name="WhatsApp" enabled={wa} onToggle={onWa}>
                    {onRecipients
                        ? <RecipientList label="Recipients" helperText={recipientHelp} value={recipients} onChange={onRecipients} />
                        : <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mt: 0.75 }}>{waSelfNote || 'Sends to the configured number.'}</Typography>}
                </ChannelPanel>
                {onTg
                    ? <ChannelPanel color="#1B7FB0" icon="✈️" name="Telegram" enabled={!!tg} disabled={tgDisabled} onToggle={onTg}>
                        {tgDisabled
                            ? <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mt: 0.75 }}>Master switch is OFF (Channels above).</Typography>
                            : <RecipientList label="Chat IDs" placeholder="-1004469659002  ·  8864070461" value={tgRecipients} onChange={onTgRecipients} />}
                      </ChannelPanel>
                    : <ChannelPanel color="#94A3B8" icon="✈️" name="Telegram" onToggle={null} chip="WhatsApp-only" />}
            </Box>
        </Paper>
    );
}

export default function MessagingSettings() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ type: '', msg: '' });
    const [testText, setTestText] = useState('Test from Nexwave admin panel');
    const [testRecipientsCsv, setTestRecipientsCsv] = useState('');
    const [testing, setTesting] = useState(false);
    const [tgTesting, setTgTesting] = useState(false);
    const [firing, setFiring] = useState(false);
    const [digestSending, setDigestSending] = useState(false);
    const [claimTestTo, setClaimTestTo] = useState('');
    const [claimSending, setClaimSending] = useState(false);
    const [announceText, setAnnounceText] = useState('');
    const [announcing, setAnnouncing] = useState(false);

    useEffect(() => {
        getClapsleySettings()
            .then(r => setCfg(r.data))
            .catch(err => setSnack({ type: 'error', msg: err?.response?.data?.message || err.message }))
            .finally(() => setLoading(false));
    }, []);

    const set = (patch) => setCfg(c => ({ ...c, ...patch }));

    const save = async () => {
        setSaving(true); setSnack({ type: '', msg: '' });
        try {
            const r = await saveClapsleySettings(cfg);
            if (r.data?.cfg) setCfg(r.data.cfg);
            setSnack({ type: 'success', msg: 'Saved. Applies on the next message send.' });
        } catch (err) { setSnack({ type: 'error', msg: err?.response?.data?.message || err.message }); }
        finally { setSaving(false); }
    };

    const fireLeaderboard = async () => {
        setFiring(true); setSnack({ type: '', msg: '' });
        try {
            const d = (await sendLeaderboardNow()).data || {};
            if (d.sent) setSnack({ type: 'success', msg: `Leaderboard sent. ${d.saleCount} sale(s) this month.` });
            else if (d.saleCount === 0) setSnack({ type: 'info', msg: 'No sales this month — nothing sent.' });
            else setSnack({ type: 'warning', msg: d.note || 'Send failed — check backend logs.' });
        } catch (err) { setSnack({ type: 'error', msg: err?.response?.data?.message || err.message }); }
        finally { setFiring(false); }
    };

    const fireTest = async () => {
        setTesting(true); setSnack({ type: '', msg: '' });
        try {
            const recipients = testRecipientsCsv.split(',').map(s => s.trim()).filter(Boolean);
            const d = (await sendClapsleyTestMessage(testText, recipients)).data || {};
            setSnack({ type: d.sent ? 'success' : 'warning', msg: d.sent ? `WhatsApp sent to ${(d.recipients || []).join(', ')}.` : (d.note || 'Send failed.') });
        } catch (err) { setSnack({ type: 'error', msg: err?.response?.data?.message || err.message }); }
        finally { setTesting(false); }
    };

    const fireTgTest = async () => {
        setTgTesting(true); setSnack({ type: '', msg: '' });
        try {
            const d = (await sendTelegramTest(testText, '')).data || {};
            setSnack({ type: d.sent ? 'success' : 'warning', msg: d.sent ? 'Telegram test sent.' : (d.note || 'Telegram send failed — check bot token / chat id.') });
        } catch (err) { setSnack({ type: 'error', msg: err?.response?.data?.message || err.message }); }
        finally { setTgTesting(false); }
    };

    const fireAnnouncement = async () => {
        if (!announceText.trim()) { setSnack({ type: 'warning', msg: 'Type the announcement body first.' }); return; }
        setAnnouncing(true); setSnack({ type: '', msg: '' });
        try {
            const d = (await sendAnnouncement(announceText)).data || {};
            setSnack({ type: d.sent ? 'success' : 'warning', msg: d.sent ? `Announcement broadcast${d.whatsapp ? ' · WhatsApp' : ''}${d.telegram ? ' · Telegram' : ''}.` : (d.note || 'Send failed.') });
            if (d.sent) setAnnounceText('');
        } catch (err) { setSnack({ type: 'error', msg: err?.response?.data?.message || err.message }); }
        finally { setAnnouncing(false); }
    };

    if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    if (!cfg) return <Alert severity="error" sx={{ m: 4 }}>{snack.msg || 'Failed to load settings.'}</Alert>;

    const tgMaster = !!cfg.enableTelegram;
    const C = (t) => <code style={{ background: '#F1F5F9', padding: '1px 5px', borderRadius: 4 }}>{t}</code>;

    return (
        <Box sx={{ width: '100%', p: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>Messaging Templates</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontFamily: PP, mt: 0.5 }}>
                    One template per event, shared across <b>WhatsApp</b> and <b>Telegram</b>. Toggle each channel per event.
                    Changes apply on the next send — no restart.
                </Typography>
                <Chip label="Super Admin only" size="small" sx={{ mt: 1, background: '#FEF3C7', color: '#92400E', fontFamily: PP, fontWeight: 600 }} />
            </Box>

            {snack.msg && <Alert severity={snack.type || 'info'} sx={{ mb: 2, fontFamily: PP }} onClose={() => setSnack({ type: '', msg: '' })}>{snack.msg}</Alert>}

            {/* ── Channels (connection) — two columns: WhatsApp | Telegram ── */}
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>Channels</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                {/* WhatsApp — connection only; recipients are set per-event below */}
                <Paper sx={{ ...cardSx, mb: 0, borderLeft: '3px solid #128C7E' }}>
                    <ChannelHeader color="#128C7E" icon="💬" name="WhatsApp (Clapsley)" />
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mt: 1 }}>Connected via Clapsley (API key in server config). <b>Recipients are set per event below</b> — each event has its own WhatsApp group / number list.</Typography>
                </Paper>
                {/* Telegram — connection only; chat ids are set per-event below */}
                <Paper sx={{ ...cardSx, mb: 0, borderLeft: '3px solid #1B7FB0' }}>
                    <ChannelHeader color="#1B7FB0" icon="✈️" name="Telegram" enabled={tgMaster} onToggle={v => set({ enableTelegram: v })} />
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP, mt: 1, mb: 1 }}>Bot @Nexwave_aibot. Master off → no Telegram posting. <b>Chat IDs are set per event below.</b></Typography>
                    <TextField size="small" fullWidth value={cfg.telegramBotToken || ''} onChange={e => set({ telegramBotToken: e.target.value })}
                        label="Bot token (@BotFather)" InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }} InputLabelProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }} />
                </Paper>
            </Box>

            {/* ── Sale-Creation Bot (@Nexwavesalesbot) — a SEPARATE bot that CREATES sales from chat ── */}
            <Paper sx={{ ...cardSx, borderLeft: '3px solid #229ED9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#229ED9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.8rem' }}>🤖</Box>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.9rem', fontWeight: 700, color: '#0F172A' }}>Sale-Creation Bot (@Nexwavesalesbot)</Typography>
                    </Box>
                    <FormControlLabel
                        control={<Switch size="small" checked={!!cfg.saleBotPolling} onChange={e => set({ saleBotPolling: e.target.checked })} />}
                        label={<Typography sx={{ fontFamily: PP, fontSize: '0.8rem', fontWeight: 600 }}>This server polls the bot</Typography>}
                        labelPlacement="start" sx={{ m: 0 }} />
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mt: 1 }}>
                    A separate bot that lets agents <b>create sales by chatting</b> (link account → paste details → confirm → sign link back). Paste its @BotFather token and turn <b>“This server polls”</b> ON to bring it live.
                </Typography>
                <Alert severity="warning" sx={{ mt: 1, py: 0, fontFamily: PP, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                    Only <b>one</b> server may poll a bot at a time. If localhost (or any other server) is still polling this bot, turn its switch OFF there first — otherwise Telegram throws a <b>409 conflict</b> and neither side receives messages.
                </Alert>
                <TextField size="small" fullWidth sx={{ mt: 1.25 }} value={cfg.saleBotToken || ''} onChange={e => set({ saleBotToken: e.target.value })}
                    label="Sale-bot token (@BotFather)" placeholder="8875686035:AA…"
                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }} InputLabelProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP, mt: 0.75 }}>
                    Agents link their own Telegram from <b>Profile → Link Telegram → Generate link code</b>, then send that code to the bot. Save applies within ~15s (no restart).
                </Typography>
            </Paper>

            {/* ── Events ── */}
            <EventCard icon="🎉" title="New-sale congrats" desc="Fires on every new Active sale."
                vars={<>{C('{agent_name}')} {C('{cobroke_name}')} {C('{kwp}')} {C('{city}')} {C('{state}')} {C('{deposit_date}')} {C('{customer}')} {C('{price}')}</>}
                wa={cfg.enableNewSale} onWa={v => set({ enableNewSale: v })}
                tg={cfg.tgEnableNewSale} onTg={v => set({ tgEnableNewSale: v })} tgDisabled={!tgMaster} tgRecipients={cfg.tgNewSaleRecipients} onTgRecipients={v => set({ tgNewSaleRecipients: v })}
                template={cfg.newSaleTemplate} onTemplate={v => set({ newSaleTemplate: v })} defaultTemplate={DEFAULT_NEW_SALE_TEMPLATE}
                recipients={cfg.newSaleRecipients} onRecipients={v => set({ newSaleRecipients: v })} recipientHelp="Group JID, personal JID, or plain phone." />

            <EventCard icon="🏆" title="Monthly leaderboard" desc="Sunday 20:00 MYT (and manual fire below)." minRows={6}
                vars={<>{C('{month}')} {C('{leaderboard}')} {C('{agent_count}')}</>}
                wa={cfg.enableWeeklyLeaderboard} onWa={v => set({ enableWeeklyLeaderboard: v })}
                tg={cfg.tgEnableLeaderboard} onTg={v => set({ tgEnableLeaderboard: v })} tgDisabled={!tgMaster} tgRecipients={cfg.tgLeaderboardRecipients} onTgRecipients={v => set({ tgLeaderboardRecipients: v })}
                template={cfg.leaderboardTemplate} onTemplate={v => set({ leaderboardTemplate: v })} defaultTemplate={DEFAULT_LEADERBOARD_TEMPLATE}
                recipients={cfg.leaderboardRecipients} onRecipients={v => set({ leaderboardRecipients: v })} />

            <EventCard icon="📢" title="Noticeboard announcement" desc="When a noticeboard post is published." minRows={5}
                vars={<>{C('{title}')} {C('{content}')} {C('{author}')} {C('{date}')}</>}
                wa={cfg.enableNotice} onWa={v => set({ enableNotice: v })}
                tg={cfg.tgEnableNotice} onTg={v => set({ tgEnableNotice: v })} tgDisabled={!tgMaster} tgRecipients={cfg.tgNoticeRecipients} onTgRecipients={v => set({ tgNoticeRecipients: v })}
                template={cfg.noticeTemplate} onTemplate={v => set({ noticeTemplate: v })} defaultTemplate={DEFAULT_NOTICE_TEMPLATE}
                recipients={cfg.noticeRecipients} onRecipients={v => set({ noticeRecipients: v })} />

            {/* ── Ready-to-Claim email (schedule + recipients). Email, not WhatsApp/Telegram —
                 goes out via Resend. Recipients here are EXTRA addresses on top of the active
                 Accountants, so external parties (e.g. Astern) can receive it. (user 2026-08-18) */}
            <Paper sx={{ ...cardSx }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>📧 Ready-to-Claim email</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mt: 0.3 }}>
                            Emails the list of sales that have reached a <b>60%</b> or <b>100%</b> customer-payment
                            milestone, so commission can be claimed from Astern. Sent by <b>email</b> (not WhatsApp
                            or Telegram). Every <b>active Accountant</b> always receives it — the addresses below are
                            sent <b>in addition</b> to them.
                        </Typography>
                    </Box>
                    <Switch checked={!!cfg.enableClaimEmail}
                        onChange={e => set({ enableClaimEmail: e.target.checked })} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
                    <TextField size="small" label="Send on days of month" value={cfg.claimEmailDays ?? ''}
                        onChange={e => set({ claimEmailDays: e.target.value })}
                        placeholder="1,16" sx={{ maxWidth: 220 }}
                        helperText="Comma-separated. Blank = 1,16"
                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.8rem' } }} />
                    <TextField size="small" type="number" label="Hour (MYT)" value={cfg.claimEmailHour ?? 9}
                        onChange={e => set({ claimEmailHour: Number(e.target.value) })}
                        inputProps={{ min: 0, max: 23 }} sx={{ maxWidth: 150 }}
                        helperText="0–23. Default 9"
                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.8rem' } }} />
                </Box>

                <RecipientList label="Additional recipients (email)"
                    helperText="Plain email addresses — these do not have to be system users."
                    placeholder="name@astern-tech.com"
                    emptyNote="(none — Accountants only)"
                    value={cfg.claimEmailRecipients} onChange={v => set({ claimEmailRecipients: v })} />

                <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mt: 1.5 }}>
                    The email carries the <b>Ready_To_Claim_Astern_&lt;date&gt;.xlsx</b> sheet as an attachment —
                    the same 14 columns as the "Ready to Claim (Astern)" button on Astern Claims, listing only
                    sales with commission still claimable at their milestone. Astern's claim contacts are
                    <b>Cc'd</b> on every send.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                    <TextField size="small" label="Send a test copy to" value={claimTestTo}
                        onChange={e => setClaimTestTo(e.target.value)}
                        placeholder="you@webbey.com.my" sx={{ minWidth: 260 }}
                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.8rem' } }} />
                    <Button size="small" variant="outlined" disabled={claimSending || !claimTestTo.includes('@')}
                        onClick={async () => {
                            setClaimSending(true); setSnack({ type: '', msg: '' });
                            try {
                                const r = await sendClaimEmailNow(claimTestTo.trim());
                                setSnack({ type: 'success', msg: r?.data?.note || 'Test sent.' });
                            } catch (err) {
                                setSnack({ type: 'error', msg: err?.response?.data?.note || err?.response?.data?.message || err.message });
                            } finally { setClaimSending(false); }
                        }}
                        sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem' }}>
                        {claimSending ? 'Sending…' : 'Send test'}
                    </Button>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP }}>
                        Goes to that address only — no Accountants, no Cc.
                    </Typography>
                </Box>
            </Paper>

            {/* ── Daily Pricing-Tools + Resources digest (Telegram, end-of-day) ── */}
            <Paper sx={{ ...cardSx }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>📋 Daily Pricing / Resources digest</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP, mt: 0.3 }}>
                            Posts one point-form summary to Telegram at <b>5:00 PM (17:00) MYT</b> — but <b>only</b> for changes in the
                            <b> Pricing Tools</b> (ATAP lists, add-ons, price history) and <b>Resources</b> (documents) sections.
                            All other changes are ignored. If nothing changed that day, nothing is sent.
                        </Typography>
                    </Box>
                    <Switch checked={!!cfg.enablePricingDigest} disabled={!tgMaster}
                        onChange={e => set({ enablePricingDigest: e.target.checked })} />
                </Box>
                {!tgMaster && <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mt: 0.75 }}>Telegram master switch is OFF (Channels above).</Typography>}
                <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mt: 0.75 }}>Sends to the same Telegram chat IDs as the Noticeboard announcement above.</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                    <Button size="small" variant="outlined" disabled={!tgMaster || digestSending}
                        onClick={async () => {
                            setDigestSending(true); setSnack({ type: '', msg: '' });
                            try {
                                const r = await sendPricingDigestNow();
                                setSnack({ type: 'success', msg: r?.data?.note || 'Digest sent.' });
                            } catch (err) {
                                setSnack({ type: 'error', msg: err?.response?.data?.note || err?.response?.data?.message || err.message });
                            } finally { setDigestSending(false); }
                        }}
                        sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem' }}>
                        {digestSending ? 'Sending…' : 'Send digest now (test)'}
                    </Button>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP }}>
                        Posts today's changes immediately — or "No changes today" so you can verify it reaches Telegram.
                    </Typography>
                </Box>
            </Paper>

            <EventCard icon="🙋" title="New-member on-boarding (→ Janny)" desc="DM the coordinator when a new user is approved."
                vars={<>{C('{name}')} {C('{phone}')} {C('{upline}')} {C('{uplinephone}')} {C('{team}')}</>}
                wa={cfg.enableOnboarding} onWa={v => set({ enableOnboarding: v })}
                tg={cfg.tgEnableOnboarding} onTg={v => set({ tgEnableOnboarding: v })} tgDisabled={!tgMaster} tgRecipients={cfg.tgOnboardingRecipients} onTgRecipients={v => set({ tgOnboardingRecipients: v })}
                template={cfg.onboardingTemplate} onTemplate={v => set({ onboardingTemplate: v })} defaultTemplate={DEFAULT_ONBOARDING_TEMPLATE}
                recipients={cfg.onboardingRecipients} onRecipients={v => set({ onboardingRecipients: v })} recipientHelp="Defaults to Janny." />

            <EventCard icon="👋" title="Welcome DM to new member" desc="Sent to the member's own WhatsApp on approval. WhatsApp only." minRows={8}
                vars={<>{C('{name}')}</>}
                wa={cfg.enableWelcomeMember} onWa={v => set({ enableWelcomeMember: v })}
                tg={null} onTg={null} waSelfNote="Sends to the new member's own number (normalised to 60xxx)."
                template={cfg.welcomeMemberTemplate} onTemplate={v => set({ welcomeMemberTemplate: v })} defaultTemplate={DEFAULT_WELCOME_MEMBER_TEMPLATE} />

            {/* Manual "System-update Announcement" removed per user (2026-08-08): the daily
                Pricing/Resources digest auto-posts the system update, so the manual
                broadcast is redundant. Backend SendAnnouncement endpoint kept (unused). */}

            {/* ── Save ── */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}
                    sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', '&:hover': { background: 'linear-gradient(135deg,#0F62CC,#0A4FA3)' } }}>
                    {saving ? 'Saving…' : 'Save settings'}
                </Button>
            </Box>

            {/* ── Test ── */}
            <Paper sx={{ ...cardSx, background: '#F8FAFC' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1.5 }}>Test send</Typography>
                <TextField size="small" fullWidth multiline minRows={2} value={testText} onChange={e => setTestText(e.target.value)} label="Test message" sx={{ mb: 1.25 }}
                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }} InputLabelProps={{ sx: { fontFamily: PP, fontSize: '0.85rem' } }} />
                <TextField size="small" fullWidth value={testRecipientsCsv} onChange={e => setTestRecipientsCsv(e.target.value)}
                    label="WhatsApp recipients (comma-sep) — empty = Default Group JID" placeholder="120363…@g.us, 60xxxxxxxxx@s.whatsapp.net" sx={{ mb: 1.5 }}
                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem' } }} InputLabelProps={{ sx: { fontFamily: PP, fontSize: '0.85rem' } }} />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<SendIcon />} onClick={fireTest} disabled={testing}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', color: '#25D366', borderColor: '#25D366' }}>{testing ? 'Sending…' : 'Test WhatsApp'}</Button>
                    <Button variant="outlined" startIcon={<SendIcon />} onClick={fireTgTest} disabled={tgTesting}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', color: '#229ED9', borderColor: '#229ED9' }}>{tgTesting ? 'Sending…' : 'Test Telegram'}</Button>
                    <Button variant="outlined" startIcon={<EmojiEventsIcon />} onClick={fireLeaderboard} disabled={firing}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', color: '#7C3AED', borderColor: '#7C3AED' }}>{firing ? 'Firing…' : 'Fire leaderboard NOW'}</Button>
                </Box>
            </Paper>
        </Box>
    );
}
